import { useMemo, useState, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { InlineNativeSelect } from "@/components/v2/InlineNativeSelect";
import { SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

export type DenseMarketStatus = "all" | "active" | "inactive";

type DenseMarketFiltersState = {
  query: string;
  status: DenseMarketStatus;
  categories: string[];
  providers: string[];
  sort: string;
};

type DenseMarketFiltersProps = {
  filters: DenseMarketFiltersState;
  categories: string[];
  providers: string[];
  sortOptions: Array<{ value: string; label: string }>;
  onChange: (filters: DenseMarketFiltersState) => void;
  onReset: () => void;
};

const statusLabels: Record<DenseMarketStatus, string> = {
  all: "All",
  active: "Active",
  inactive: "Inactive",
};

function DenseFilterContent({ filters, categories, providers, sortOptions, onChange, onReset }: DenseMarketFiltersProps) {
  const handleStatusChange = (status: DenseMarketStatus) => {
    if (filters.status === status) return;
    onChange({ ...filters, status });
  };

  const toggleCategory = (category: string) => {
    const next = filters.categories.includes(category)
      ? filters.categories.filter((value) => value !== category)
      : [...filters.categories, category];
    onChange({ ...filters, categories: next });
  };

  const toggleProvider = (provider: string) => {
    const next = filters.providers.includes(provider)
      ? filters.providers.filter((value) => value !== provider)
      : [...filters.providers, provider];
    onChange({ ...filters, providers: next });
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filters, query: event.target.value });
  };

  const activeCount = useMemo(() => {
    let n = 0;
    if (filters.query?.trim()) n += 1;
    if (filters.status !== "active") n += 1;
    if (filters.sort !== "volume") n += 1;
    if (filters.categories.length) n += 1;
    if (filters.providers.length) n += 1;
    return n;
  }, [filters]);

  return (
    <div className="font-sans text-sm text-muted-foreground">
      <div className="flex items-center justify-between">
        <div className="text-base font-semibold text-foreground">Filters</div>
        <button
          type="button"
          className="text-[0.65rem] uppercase font-mono tracking-wide text-muted-foreground/80 hover:text-foreground"
          onClick={onReset}
        >
          Clear all
        </button>
      </div>

      {activeCount > 0 && (
        <div className="mt-2 text-[0.65rem] font-mono uppercase tracking-wide text-muted-foreground/80">
          {activeCount} active filter{activeCount === 1 ? "" : "s"}
        </div>
      )}

      <div className="mt-5 space-y-1">
        <label className="block text-[0.75rem] uppercase font-mono tracking-wide text-muted-foreground">Search Asset</label>
        <input
          value={filters.query}
          onChange={handleSearchChange}
          placeholder="TSLA, BTC, etc."
          className="w-full border border-border/70 bg-background/60 px-2.5 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
        />
      </div>

      <div className="mt-5 space-y-1">
        <label className="block text-[0.75rem] uppercase font-mono tracking-wide text-muted-foreground">Sort</label>
        <InlineNativeSelect
          value={filters.sort}
          onChange={(event) => onChange({ ...filters, sort: event.target.value })}
          wrapperClassName="w-full"
          className="w-full border border-border/70 bg-background/60 pl-2.5 py-2 text-xs text-foreground focus:border-primary focus:outline-none"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </InlineNativeSelect>
      </div>

      <div className="mt-5 space-y-2">
        <div className="text-[0.75rem] uppercase font-mono tracking-wide text-muted-foreground">Status</div>
        <div className="flex flex-wrap gap-1.5 font-mono">
          {(Object.keys(statusLabels) as DenseMarketStatus[]).map((status) => (
            <button
              key={status}
              type="button"
              onClick={() => handleStatusChange(status)}
              className={cn(
                "border px-3 py-1 text-[0.68rem] uppercase tracking-wide transition",
                filters.status === status
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                  : "border-border/70 text-muted-foreground"
              )}
            >
              {statusLabels[status]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="text-[0.75rem] uppercase font-mono tracking-wide text-muted-foreground">Category</div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => toggleCategory(category)}
              className={cn(
                "border px-2 py-1 text-[0.68rem] font-mono uppercase tracking-wide transition",
                filters.categories.includes(category)
                  ? "border-emerald-500 bg-emerald-500/10 text-emerald-300"
                  : "border-border/70 text-muted-foreground"
              )}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="text-[0.75rem] uppercase font-mono tracking-wide text-muted-foreground">Providers</div>
        <div className="flex flex-wrap gap-1.5">
          {providers.map((provider) => (
            <button
              key={provider}
              type="button"
              onClick={() => toggleProvider(provider)}
              className={cn(
                "border px-2 py-1 text-[0.68rem] font-mono uppercase tracking-wide transition",
                filters.providers.includes(provider)
                  ? "border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-300"
                  : "border-border/70 text-muted-foreground"
              )}
            >
              {provider}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function DenseMarketFiltersDesktop(props: DenseMarketFiltersProps) {
  return (
    <aside className="sticky top-5 self-start">
      <div className="max-h-[calc(100vh-2.5rem)] overflow-y-auto border border-border/70 bg-card/90 p-4">
        <DenseFilterContent {...props} />
      </div>
    </aside>
  );
}

export function DenseMarketFiltersMobile(props: DenseMarketFiltersProps) {
  const [open, setOpen] = useState(false);

  const activeCount = useMemo(() => {
    let n = 0;
    if (props.filters.query?.trim()) n += 1;
    if (props.filters.status !== "active") n += 1;
    if (props.filters.sort !== "volume") n += 1;
    if (props.filters.categories.length) n += 1;
    if (props.filters.providers.length) n += 1;
    return n;
  }, [props.filters]);

  return (
    <div className="lg:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2 font-mono rounded-none h-8 px-2.5">
            <SlidersHorizontal className="h-4 w-4" />
            <span className="text-[0.7rem] uppercase">Filters</span>
            {activeCount > 0 && (
              <span className="border border-border/70 bg-background/80 px-1.5 py-0.5 text-[0.6rem]">
                {activeCount}
              </span>
            )}
          </Button>
        </SheetTrigger>

        <SheetContent side="bottom" className="p-0 rounded-none border-t border-border/70">
          <SheetHeader className="border-b border-border/60 p-3">
            <SheetTitle className="font-mono uppercase text-xs tracking-wide">Filters</SheetTitle>
          </SheetHeader>

          <div className="max-h-[75vh] overflow-y-auto p-4">
            <DenseFilterContent {...props} />

            <div className="mt-5 flex gap-2">
              <Button
                variant="outline"
                className="w-1/2 rounded-none"
                onClick={() => {
                  props.onReset();
                  setOpen(false);
                }}
              >
                Reset
              </Button>
              <Button className="w-1/2 rounded-none" onClick={() => setOpen(false)}>
                Apply
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
